import { MonitorDashboardResponse, MonitorMeta, MonitorQueryState } from "./types";
import { buildDashboardParams } from "./state";

function withCsrfHeaders(headers: Record<string, string> = {}) {
  const csrfTag = document.querySelector("meta[name='csrf-token']");
  if (csrfTag) {
    headers["X-CSRF-Token"] = csrfTag.getAttribute("content") || "";
  }
  return headers;
}

function withPlaceholder(urlTemplate: string, placeholder: string, selectedIds: number[]) {
  return urlTemplate.replace(placeholder, selectedIds.join(","));
}

async function postForm(url: string, body = "note="): Promise<any> {
  const response = await fetch(url, {
    method: "POST",
    credentials: "same-origin",
    headers: withCsrfHeaders({ "Content-Type": "application/x-www-form-urlencoded; charset=UTF-8" }),
    body: body
  });

  if (!response.ok) {
    let details = "";
    try {
      const data = await response.json();
      if (Array.isArray(data)) {
        details = data.join(", ");
      } else if (data && typeof data === "object") {
        details = data.error || data.message || JSON.stringify(data);
      }
    } catch (_err) {
      try {
        details = await response.text();
      } catch (_err2) {
        details = "";
      }
    }

    throw new Error(details ? `Request failed with status ${response.status}: ${details}` : `Request failed with status ${response.status}`);
  }

  try {
    return await response.json();
  } catch (_err) {
    return null;
  }
}

export async function fetchMonitorDashboardData(
  dataUrl: string,
  state: MonitorQueryState,
  meta: MonitorMeta | null
): Promise<MonitorDashboardResponse> {
  const params = buildDashboardParams(state);
  const response = await fetch(`${dataUrl}?${params.toString()}`, {
    credentials: "same-origin",
    headers: withCsrfHeaders()
  });

  if (!response.ok) {
    throw new Error(`Monitor data request failed with status ${response.status}`);
  }

  const data = await response.json();
  if (meta && !data.meta) {
    data.meta = meta;
  }
  return data;
}

export async function postMassSignSelected(urlTemplate: string, selectedIds: number[]): Promise<any> {
  if (selectedIds.length === 0) {
    return null;
  }

  return postForm(withPlaceholder(urlTemplate, "__PATIENT_IDS__", selectedIds));
}

export async function postMassSignAll(url: string): Promise<any> {
  return postForm(url);
}

export async function postUnlockSelected(urlTemplate: string, selectedIds: number[]): Promise<any> {
  if (selectedIds.length === 0) {
    return null;
  }

  return postForm(withPlaceholder(urlTemplate, "__PATIENT_IDS__", selectedIds));
}

// HHMCOVID-5632 (BR-019) — persist the user's hidden Monitoring Dashboard columns.
// Returns the server's canonical hidden-column list (`{ hidden_columns: [...] }`),
// which may differ from what was sent if the server stripped a required/unknown
// key; the caller reconciles its state to the response.
export async function postColumnVisibility(url: string, hiddenKeys: string[]): Promise<{ hidden_columns: string[] } | null> {
  const params = new URLSearchParams();
  if (hiddenKeys.length === 0) {
    // Ensure the param is present (and empty) so the server clears the list.
    params.append("hidden_columns[]", "");
  } else {
    hiddenKeys.forEach((key) => params.append("hidden_columns[]", key));
  }
  return postForm(url, params.toString());
}
